// #2762: 「AIに任せる業務」. Business tasks as cards; opening one shows its
// steps, the services it uses, its preferences, and the tools each connected
// AI declared for it. Edits go to the shared profile draft through `onField`
// and are saved with the rest of the settings.

import { LitElement, html, nothing } from 'lit';
import { currentLang, t } from '../i18n';
import { api } from '../api';
import { userTimezone } from '../state';
import { getAiWorkVerbViews, type ProfileDraft } from '../profile';
import { defineZenComponent } from './register';
import { parseAiWorkDraft, serializeAiWorkDraft, type AiWorkDraft, type AiWorkVerbView } from '../ai-work-draft-logic';
import {
  ADDABLE_CARD_VERBS,
  aiName,
  cardAis,
  cardServices,
  cardToolsFor,
  declaredDateLabel,
  groupState,
  normalizeWorkService,
  serviceLabel,
  toolActionKind,
  toolGroupService,
  stepState,
  stepStateMixed,
  toolHasOwnState,
  toolState,
  withCardAdded,
  withCardRemoved,
  withCardServiceAdded,
  withCardServiceRemoved,
  withServiceToolsState,
  withStepState,
  withToolReset,
  withToolState,
  workCards,
  WORK_SERVICE_SUGGESTIONS,
  type DeclaredAi,
  type DeclaredTool,
  type WorkCard,
  type WorkStepState,
} from '../ai-work-cards-logic';

type FieldSetter = (key: keyof ProfileDraft, value: string) => void;

const STATE_ICON: Record<WorkStepState, string> = { auto: 'smart_toy', check: 'how_to_reg', principal: 'person' };
const STATE_LABEL: Record<WorkStepState, string> = { auto: 'aiWorkStateAuto', check: 'aiWorkStateCheck', principal: 'aiWorkStatePrincipal' };
const STATE_SHORT: Record<WorkStepState, string> = { auto: 'aiWorkStateShortAuto', check: 'aiWorkStateShortCheck', principal: 'aiWorkStateShortPrincipal' };
const STATES: readonly WorkStepState[] = ['auto', 'check', 'principal'];

const ACCOUNTING_FIELD: Record<'payments' | 'invoices' | 'expenses', keyof ProfileDraft> = {
  payments: 'prefAccPaymentsExec',
  invoices: 'prefAccInvoicesExec',
  expenses: 'prefAccExpensesExec',
};

export class ZenAiWorkCards extends LitElement {
  static properties = {
    formData: { attribute: false },
    onField: { attribute: false },
    openCardId: { state: true },
    activeAiId: { state: true },
    ais: { state: true },
    aisState: { state: true },
    adding: { state: true },
    newWhat: { state: true },
    newSteps: { state: true },
  };

  declare formData: ProfileDraft;
  declare onField: FieldSetter;
  declare openCardId: string | null;
  declare activeAiId: string | null;
  declare ais: DeclaredAi[];
  declare aisState: 'loading' | 'ready' | 'error';
  declare adding: boolean;
  declare newWhat: string;
  declare newSteps: string[];

  constructor() {
    super();
    this.onField = () => {};
    this.openCardId = null;
    this.activeAiId = null;
    this.ais = [];
    this.aisState = 'loading';
    this.adding = false;
    this.newWhat = '';
    this.newSteps = [];
  }

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    void this.loadAis();
  }

  private async loadAis() {
    this.aisState = 'loading';
    try {
      const data = await api('/api/profile/ai-tools') as { ais?: DeclaredAi[] };
      this.ais = Array.isArray(data?.ais) ? data.ais : [];
      this.aisState = 'ready';
    } catch {
      this.ais = [];
      this.aisState = 'error';
    }
  }

  private get draft(): AiWorkDraft {
    return parseAiWorkDraft(this.formData?.prefAiWork ?? '');
  }

  private get views(): AiWorkVerbView[] {
    return getAiWorkVerbViews();
  }

  private setDraft(next: AiWorkDraft) {
    this.onField('prefAiWork', serializeAiWorkDraft(next));
  }

  private cardName(card: WorkCard): string {
    return card.nameKey ? t(card.nameKey) as string : card.what;
  }

  private checkFixed(how: string): boolean {
    return this.views.find((view) => view.how === how)?.content_check_fixed === true;
  }

  private openCard(card: WorkCard) {
    this.openCardId = card.id;
    this.activeAiId = null;
    this.updateComplete.then(() => this.querySelector<HTMLElement>('.ai-work-detail h4')?.focus());
  }

  private closeCard() {
    const id = this.openCardId;
    this.openCardId = null;
    this.updateComplete.then(() => this.querySelector<HTMLElement>(`[data-card-open="${id}"]`)?.focus());
  }

  private renderStateIcon(state: WorkStepState) {
    return html`<span class="mi mi-sm ${state === 'principal' ? 'mi-fill' : ''}" aria-hidden="true">${STATE_ICON[state]}</span>`;
  }

  /** On a card in the list: what each step is set to, read only; the card's detail changes it. */
  private renderStepSummary(card: WorkCard, draft: AiWorkDraft) {
    const views = this.views;
    return html`
      <div class="ai-work-steps">
        ${card.steps.map((step, index) => {
          const state = stepState(card, step.how, draft, views);
          return html`
            ${index > 0 ? html`<span class="mi mi-sm ai-work-step-arrow" aria-hidden="true">chevron_right</span>` : nothing}
            <span class="ai-work-step" data-state=${state} data-how=${step.how}>
              ${t(step.labelKey)}
              <span class="ai-work-who">${this.renderStateIcon(state)}${t(STATE_SHORT[state])}</span>
            </span>
          `;
        })}
      </div>
    `;
  }

  /** In a card's detail: one row per step with the three choices side by side. */
  private renderStepRows(card: WorkCard, draft: AiWorkDraft) {
    const views = this.views;
    return html`
      <div class="ai-work-step-rows">
        ${card.steps.map((step) => {
          const state = stepState(card, step.how, draft, views);
          const mixed = stepStateMixed(card, step.how, draft, views);
          const label = t(step.labelKey) as string;
          const fixed = this.checkFixed(step.how);
          return html`
            <div class="ai-work-step-row" data-how=${step.how}>
              <span class="ai-work-step-name">
                ${label}
                ${mixed ? html`<small class="field-hint">${t('aiWorkStepMixed')}</small>` : nothing}
                ${fixed ? html`<small class="field-hint">${t('aiWorkStepFixed')}</small>` : nothing}
              </span>
              ${this.renderStateRadios(label, mixed ? null : state, fixed,
                (next) => this.setDraft(withStepState(card, step.how, next, draft, views)), true)}
            </div>
          `;
        })}
      </div>
    `;
  }

  private renderAiLine(card: WorkCard, draft: AiWorkDraft) {
    if (this.aisState !== 'ready') return nothing;
    if (cardServices(card, draft).length === 0) {
      return html`<span class="ai-work-ai-line">${t('aiWorkNoServiceYet')}</span>`;
    }
    const ais = cardAis(card, this.ais, draft);
    return html`
      <span class="ai-work-ai-line">
        <span class="mi mi-sm" aria-hidden="true">${ais.length ? 'smart_toy' : 'block'}</span>
        ${ais.length ? t('aiWorkAisCan', { names: ais.map(aiName).join('・') }) : t('aiWorkNoAiYet')}
      </span>
    `;
  }

  private renderServiceChips(card: WorkCard, draft: AiWorkDraft, removable: boolean) {
    const services = cardServices(card, draft);
    return services.map((service) => html`
      <span class="ai-work-chip">
        ${serviceLabel(service, currentLang)}
        ${removable ? html`
          <button type="button" class="ai-work-icon-btn" aria-label=${t('aiWorkServiceRemove', { service: serviceLabel(service, currentLang) })}
            @click=${() => this.setDraft(withCardServiceRemoved(card, service, draft, this.views))}>
            <span class="mi mi-xs" aria-hidden="true">close</span>
          </button>
        ` : nothing}
      </span>
    `);
  }

  private renderCard(card: WorkCard, draft: AiWorkDraft) {
    const name = this.cardName(card);
    return html`
      <section class="ai-work-card" data-card=${card.id}>
        <div class="ai-work-card-head">
          <span class="mi" aria-hidden="true">${card.icon}</span>
          <div class="ai-work-card-title">
            <h4>${name}</h4>
            <span class="ai-work-chips">${this.renderServiceChips(card, draft, false)}</span>
          </div>
          <button type="button" class="ai-work-icon-btn" data-card-open=${card.id}
            aria-label=${t('aiWorkOpenCard', { name })} @click=${() => this.openCard(card)}>
            <span class="mi" aria-hidden="true">chevron_right</span>
          </button>
        </div>
        ${this.renderStepSummary(card, draft)}
        ${this.renderAiLine(card, draft)}
      </section>
    `;
  }

  private renderAddForm(draft: AiWorkDraft) {
    if (!this.adding) {
      return html`
        <button type="button" class="ai-work-add" @click=${() => { this.adding = true; }}>
          <span class="mi" aria-hidden="true">add</span>${t('aiWorkAddCard')}
        </button>
      `;
    }
    const submit = () => {
      const next = withCardAdded(draft, this.newWhat, this.newSteps);
      if (next === draft) return;
      this.setDraft(next);
      this.adding = false;
      this.newWhat = '';
      this.newSteps = [];
    };
    return html`
      <section class="ai-work-card ai-work-add-form">
        <label class="field-label" for="aiWorkNewCardName">${t('aiWorkAddCardName')}</label>
        <input type="text" id="aiWorkNewCardName" maxlength="40" .value=${this.newWhat} placeholder=${t('aiWorkAddCardNamePlaceholder')}
          @input=${(e: Event) => { e.stopPropagation(); this.newWhat = (e.target as HTMLInputElement).value; }}
          @change=${(e: Event) => e.stopPropagation()}>
        <fieldset class="ai-work-verbs">
          <legend>${t('aiWorkAddCardSteps')}</legend>
          ${ADDABLE_CARD_VERBS.map((how) => html`
            <label>
              <input type="checkbox" .checked=${this.newSteps.includes(how)}
                @change=${(e: Event) => {
                  e.stopPropagation();
                  const on = (e.target as HTMLInputElement).checked;
                  this.newSteps = on ? [...this.newSteps, how] : this.newSteps.filter((v) => v !== how);
                }}>
              ${t(`aiWorkVerb_${how}`)}
            </label>
          `)}
        </fieldset>
        <div class="ai-work-actions">
          <button type="button" class="btn btn-sm" ?disabled=${!this.newWhat.trim() || this.newSteps.length === 0} @click=${submit}>${t('aiWorkAddCardSubmit')}</button>
          <button type="button" class="btn btn-sm btn-outline" @click=${() => { this.adding = false; }}>${t('cancel')}</button>
        </div>
      </section>
    `;
  }

  private renderList(draft: AiWorkDraft) {
    return html`
      <div class="ai-work-intro">
        <p class="field-hint">${t('aiWorkCardsDesc')}</p>
      </div>
      <div class="ai-work-grid">
        ${workCards(draft).map((card) => this.renderCard(card, draft))}
        ${this.renderAddForm(draft)}
      </div>
    `;
  }

  private renderSelect(id: keyof ProfileDraft, labelKey: string, options: Array<[string, string]>, hintKey?: string) {
    const value = this.formData[id];
    return html`
      <div class="pref-field">
        <label for=${id}>${t(labelKey)}</label>
        <select id=${id} @change=${(e: Event) => this.onField(id, (e.target as HTMLSelectElement).value)}>
          ${options.map(([v, key]) => html`<option value=${v} ?selected=${v === value}>${t(key)}</option>`)}
        </select>
        ${hintKey ? html`<small class="field-hint">${t(hintKey)}</small>` : nothing}
      </div>
    `;
  }

  // Preferences that belong to one business task, in their existing profile fields.
  private renderPrefs(card: WorkCard) {
    if (!card.prefs) return nothing;
    let body;
    if (card.prefs === 'mail') {
      body = this.renderSelect('prefCommExternal', 'prefCommExternal', [
        ['formal', 'prefCommFormal'], ['business_casual', 'prefCommBusinessCasual'], ['auto', 'prefCommAuto'],
      ], 'prefCommFallbackHint');
    } else if (card.prefs === 'chat') {
      body = this.renderSelect('prefCommInternal', 'prefCommInternal', [
        ['casual', 'prefCommCasual'], ['formal', 'prefCommFormal2'], ['auto', 'prefCommAuto2'],
      ], 'prefCommFallbackHint');
    } else if (card.prefs === 'schedule') {
      body = html`
        ${this.renderSelect('prefMeetingDefaultAttendance', 'prefMeetingDefaultAttendance', [
          ['auto', 'prefMeetingDefaultAttendanceAuto'], ['in_person', 'prefMeetingDefaultAttendanceInPerson'], ['online', 'prefMeetingDefaultAttendanceOnline'],
        ], 'prefMeetingDefaultAttendanceHint')}
        ${this.renderSelect('prefEventDefaultAttendance', 'prefEventDefaultAttendance', [
          ['auto', 'prefEventDefaultAttendanceAuto'], ['in_person', 'prefEventDefaultAttendanceInPerson'], ['online', 'prefEventDefaultAttendanceOnline'],
        ], 'prefEventDefaultAttendanceHint')}
        ${this.renderSelect('prefTravelMode', 'prefTravelMode', [
          ['train', 'prefTravelTrain'], ['car', 'prefTravelCar'], ['plane', 'prefTravelPlane'], ['undecided', 'prefTravelUndecided'],
        ], 'prefTravelModeHint')}
      `;
    } else {
      const field = ACCOUNTING_FIELD[card.prefs];
      const value = this.formData[field];
      const known = ['', 'self', 'delegate', 'none'];
      body = html`
        <div class="pref-field">
          <label for=${field}>${t('aiWorkPrefWho')}</label>
          <select id=${field} @change=${(e: Event) => this.onField(field, (e.target as HTMLSelectElement).value)}>
            <option value="" ?selected=${value === ''}>${t('aiWorkPrefWhoUnset')}</option>
            <option value="self" ?selected=${value === 'self'}>${t('aiWorkPrefWhoSelf')}</option>
            <option value="delegate" ?selected=${value === 'delegate'}>${t('aiWorkPrefWhoDelegate')}</option>
            <option value="none" ?selected=${value === 'none'}>${t('aiWorkPrefWhoNone')}</option>
            ${known.includes(value) ? nothing : html`<option value=${value} selected>${value}</option>`}
          </select>
          <small class="field-hint">${t('aiWorkPrefWhoHint')}</small>
        </div>
      `;
    }
    return html`
      <div class="ai-work-block">
        <h5>${t('aiWorkPrefs')}</h5>
        ${body}
      </div>
    `;
  }

  private renderServices(card: WorkCard, draft: AiWorkDraft) {
    const add = (input: HTMLInputElement) => {
      const service = normalizeWorkService(input.value);
      if (!service) return;
      this.setDraft(withCardServiceAdded(card, service, draft, this.views));
      input.value = '';
    };
    return html`
      <div class="ai-work-block">
        <h5>${t(card.sites ? 'aiWorkSites' : 'aiWorkServices')}</h5>
        <div class="ai-work-chips">${this.renderServiceChips(card, draft, true)}</div>
        ${card.sites ? nothing : html`
          <datalist id="aiWorkServiceSuggestions">
            ${WORK_SERVICE_SUGGESTIONS.map((service) => html`<option value=${service}>${serviceLabel(service, currentLang)}</option>`)}
          </datalist>
        `}
        <div class="ai-work-service-add">
          <span class="ai-work-service-field">
            <input type="text" id="aiWorkServiceInput" list=${card.sites ? nothing : 'aiWorkServiceSuggestions'}
              aria-label=${t(card.sites ? 'aiWorkSiteAdd' : 'aiWorkServiceAdd')}
              placeholder=${card.sites ? t('aiWorkSitesPlaceholder') : 'gmail, freee_invoice, notion'}
              @input=${(e: Event) => e.stopPropagation()}
              @change=${(e: Event) => e.stopPropagation()}
              @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); add(e.target as HTMLInputElement); } }}>
            ${card.sites ? nothing : html`<span class="mi mi-sm" aria-hidden="true">expand_more</span>`}
          </span>
          <button type="button" class="btn btn-sm btn-outline"
            @click=${() => add(this.querySelector<HTMLInputElement>('#aiWorkServiceInput')!)}>
            <span class="mi mi-sm" aria-hidden="true">add</span>${t(card.sites ? 'aiWorkSiteAdd' : 'aiWorkServiceAdd')}
          </button>
        </div>
        <small class="field-hint">${t(card.sites ? 'aiWorkSitesHint' : 'aiWorkServicesHint')}</small>
      </div>
    `;
  }

  private renderStateRadios(name: string, state: WorkStepState | null, fixed: boolean, onPick: (state: WorkStepState) => void, withText = false) {
    return html`
      <span class="ai-work-radios ${withText ? 'ai-work-radios-text' : ''}" role="radiogroup" aria-label=${name}>
        ${STATES.map((option) => {
          const disabled = option === 'auto' && fixed;
          return html`
            <button type="button" role="radio" aria-checked=${state === option ? 'true' : 'false'} ?disabled=${disabled}
              aria-label=${t(STATE_LABEL[option])} title=${t(STATE_LABEL[option])} data-state=${option}
              @click=${() => onPick(option)}>
              ${this.renderStateIcon(option)}${withText ? html`<span>${t(STATE_SHORT[option])}</span>` : nothing}
            </button>
          `;
        })}
      </span>
    `;
  }

  private renderTools(card: WorkCard, draft: AiWorkDraft) {
    const views = this.views;
    let body;
    if (this.aisState === 'loading') {
      body = html`<p class="field-hint">${t('aiWorkToolsLoading')}</p>`;
    } else if (this.aisState === 'error') {
      body = html`<p class="field-hint">${t('aiWorkToolsError')}</p>`;
    } else if (!card.sites && cardServices(card, draft).length === 0) {
      body = html`<p class="field-hint">${t('aiWorkToolsNeedService')}</p>`;
    } else {
      const ais = cardAis(card, this.ais, draft);
      if (ais.length === 0) {
        body = html`<p class="field-hint">${t('aiWorkToolsNone')}</p>`;
      } else {
        const active = ais.find((ai) => ai.client_id === this.activeAiId) ?? ais[0];
        const tools = cardToolsFor(card, active, draft);
        const byService = new Map<string, DeclaredTool[]>();
        for (const tool of tools) {
          const key = toolGroupService(tool);
          byService.set(key, [...(byService.get(key) ?? []), tool]);
        }
        const stepLabel = (how: string) => {
          const s = card.steps.find((row) => row.how === how);
          return s ? t(s.labelKey) as string : t(`aiWorkVerb_${how}`) as string;
        };
        body = html`
          <div class="ai-work-tabs" role="tablist">
            ${ais.map((ai) => html`
              <button type="button" role="tab" aria-selected=${ai === active ? 'true' : 'false'}
                @click=${() => { this.activeAiId = ai.client_id; }}>${aiName(ai)}</button>
            `)}
          </div>
          <p class="ai-work-declared">${t('aiWorkLastDeclared', { date: declaredDateLabel(active.declared_at, userTimezone), name: aiName(active) })}</p>
          ${[...byService.entries()].map(([service, group]) => {
            const shared = groupState(active, group, draft, views);
            const groupFixed = group.every((tool) => this.checkFixed(tool.how));
            return html`
              <div class="ai-work-tool-group" data-service=${service}>
                <div class="ai-work-tool-group-head">
                  <h6>${group[0].for_service ? t('aiWorkViaBrowser', { service: serviceLabel(service, currentLang) }) : serviceLabel(service, currentLang)}</h6>
                  <span class="ai-work-bulk">
                    ${t('aiWorkBulk')}
                    ${this.renderStateRadios(t('aiWorkBulkAria', { service: serviceLabel(service, currentLang) }) as string, shared, groupFixed,
                      (state) => this.setDraft(withServiceToolsState(active, group, state, draft, views)))}
                  </span>
                </div>
                ${group.map((tool) => {
                  const state = toolState(active, tool, draft, views);
                  const kind = toolActionKind(tool.tool_name);
                  const label = kind ? t('aiWorkToolLabel', { step: stepLabel(tool.how), action: t(`aiWorkToolAction_${kind}`) as string }) as string : stepLabel(tool.how);
                  return html`
                    <div class="ai-work-tool" data-tool=${tool.tool_name}>
                      <span class="ai-work-tool-text">
                        <span>${label}</span>
                        <small>${tool.tool_name}</small>
                      </span>
                      ${toolHasOwnState(active, tool, draft) ? html`
                        <button type="button" class="ai-work-link" @click=${() => this.setDraft(withToolReset(active, tool, draft))}>${t('aiWorkToolReset')}</button>
                      ` : nothing}
                      ${this.renderStateRadios(label, state, this.checkFixed(tool.how),
                        (next) => this.setDraft(withToolState(active, tool, next, draft, views)))}
                    </div>
                  `;
                })}
              </div>
            `;
          })}
        `;
      }
    }
    return html`
      <div class="ai-work-block">
        <h5>${t('aiWorkTools')}</h5>
        <p class="field-hint">${t('aiWorkToolsHint')}</p>
        ${body}
      </div>
    `;
  }

  private renderDetail(card: WorkCard, draft: AiWorkDraft) {
    const name = this.cardName(card);
    return html`
      <div class="ai-work-detail" data-card-detail=${card.id}>
        <nav class="ai-work-breadcrumb" aria-label=${t('aiWorkBreadcrumb')}>
          <button type="button" class="ai-work-link" @click=${() => this.closeCard()}>${t('settingsNavAiWork')}</button>
          <span class="mi mi-sm" aria-hidden="true">chevron_right</span>
          <span aria-current="page">${name}</span>
        </nav>
        <h4 tabindex="-1"><span class="mi" aria-hidden="true">${card.icon}</span>${name}</h4>
        <div class="ai-work-block">
          <h5>${t('aiWorkSteps')}</h5>
          ${this.renderStepRows(card, draft)}
        </div>
        ${this.renderServices(card, draft)}
        ${this.renderPrefs(card)}
        ${this.renderTools(card, draft)}
        ${card.added ? html`
          <button type="button" class="btn btn-sm btn-outline" @click=${() => { this.setDraft(withCardRemoved(card, draft)); this.openCardId = null; }}>
            <span class="mi mi-sm" aria-hidden="true">delete</span>${t('aiWorkRemoveCard')}
          </button>
        ` : nothing}
      </div>
    `;
  }

  render() {
    const draft = this.draft;
    const card = this.openCardId ? workCards(draft).find((row) => row.id === this.openCardId) : undefined;
    return html`<div class="ai-work-cards">${card ? this.renderDetail(card, draft) : this.renderList(draft)}</div>`;
  }
}

defineZenComponent('zen-ai-work-cards', ZenAiWorkCards);
